import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

export const marketApi = {
  getPublic: async (params = {}) => {
    const response = await api.get('/markets', { params });
    return response.data;
  },
  getByCountry: async (countryCode) => {
    const response = await api.get(`/markets/${countryCode}`);
    return response.data;
  },
  getAdminAll: async (params = {}) => {
    const response = await api.get('/admin/markets', { params });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/markets', data);
    return response.data;
  },
  update: async ({ id, data }) => {
    const response = await api.put(`/admin/markets/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/markets/${id}`);
    return response.data;
  },
  setStatus: async ({ id, status }) => {
    const response = await api.patch(`/admin/markets/${id}/status`, { status });
    return response.data;
  },
  syncPacks: async (payload = {}) => {
    const response = await api.post('/admin/markets/sync-packs', payload);
    return response.data;
  },
};

export const usePublicMarkets = (params = {}) =>
  useQuery({
    queryKey: ['markets', params],
    queryFn: () => marketApi.getPublic(params),
    staleTime: 5 * 60 * 1000,
  });

export const useMarketByCountry = (countryCode) =>
  useQuery({
    queryKey: ['market', countryCode],
    queryFn: () => marketApi.getByCountry(countryCode),
    enabled: !!countryCode && String(countryCode).length === 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const useAdminMarkets = (params = {}) =>
  useQuery({
    queryKey: ['admin-markets', params],
    queryFn: () => marketApi.getAdminAll(params),
    staleTime: 60 * 1000,
  });

export const useCreateMarket = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: marketApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
      queryClient.invalidateQueries({ queryKey: ['markets'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useUpdateMarket = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: marketApi.update,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
      queryClient.invalidateQueries({ queryKey: ['markets'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useDeleteMarket = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: marketApi.delete,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
      queryClient.invalidateQueries({ queryKey: ['markets'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useSetMarketStatus = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: marketApi.setStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
      queryClient.invalidateQueries({ queryKey: ['markets'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useSyncMarketPacks = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: marketApi.syncPacks,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
      queryClient.invalidateQueries({ queryKey: ['markets'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};
